import React, { useState } from 'react';
import styles from './ParentLogin.module.css';

export default function ParentLogin({ onLogin }) {
  const [mode, setMode] = useState('login');         // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote]   = useState('');             // success/info banner
  const [loading, setLoading] = useState(false);

  // Contact-card state for the "Need an account?" button.
  const [contactOpen, setContactOpen]       = useState(false);
  const [contact, setContact]               = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setNote('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/parent/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed'); return; }
      localStorage.setItem('ngs_parent_token', data.token);
      onLogin(data);
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setNote('');
    setLoading(true);
    try {
      const res = await fetch('/api/parent/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Could not send reset link');
        return;
      }
      setNote("If that email is on file, you'll get a reset link within a minute.");
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openContact = async () => {
    setContactOpen(true);
    if (contact == null) {
      setContactLoading(true);
      try {
        const res = await fetch('/api/coach-contact');
        if (res.ok) setContact(await res.json());
        else setContact({});
      } catch { setContact({}); }
      finally { setContactLoading(false); }
    }
  };

  const linkBtn = {
    background: 'none', border: 'none', color: '#C04A0E', fontWeight: 600,
    cursor: 'pointer', padding: 0, fontSize: '0.85rem',
  };
  const noteStyle = {
    background: '#e8f5e9', color: '#2e7d32',
    padding: '10px 12px', borderRadius: 6, fontSize: '0.85rem', marginBottom: 12,
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div role="img" aria-label="Next Gen Sports" className={styles.logo} />
        </div>
        <form
          className={styles.cardBody}
          onSubmit={mode === 'login' ? handleLogin : handleForgot}
          noValidate
        >
          <h2 className={styles.title}>Parent Portal</h2>
          <p className={styles.subtitle}>
            {mode === 'login'
              ? 'Sign in to view your account'
              : "Enter your email and we'll send you a reset link."}
          </p>

          {error && <div className={styles.error}>{error}</div>}
          {note && <div style={noteStyle}>{note}</div>}

          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <input className={styles.input} type="email" placeholder="you@email.com"
              value={email} onChange={e => setEmail(e.target.value)} disabled={loading} />
          </div>

          {mode === 'login' && (
            <div className={styles.field}>
              <label className={styles.label}>Password</label>
              <div className={styles.passWrap}>
                <input className={styles.input} type={showPass ? 'text' : 'password'} placeholder="Your password"
                  value={password} onChange={e => setPassword(e.target.value)} disabled={loading} />
                <button type="button" className={styles.passToggle} onClick={() => setShowPass(!showPass)}>
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          )}

          <button type="submit" className={styles.submit}
            disabled={loading || !email || (mode === 'login' && !password)}>
            {loading
              ? (mode === 'login' ? 'Signing in...' : 'Sending...')
              : (mode === 'login' ? 'Sign In'        : 'Send Reset Link')}
          </button>

          <div style={{ textAlign: 'center', marginTop: 14 }}>
            {mode === 'login' ? (
              <button type="button" style={linkBtn} onClick={() => switchMode('forgot')}>
                Forgot your password?
              </button>
            ) : (
              <button type="button" style={linkBtn} onClick={() => switchMode('login')}>
                ← Back to sign in
              </button>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: '0.82rem', color: '#999', lineHeight: 1.5 }}>
            Need an account?{' '}
            <button type="button" onClick={openContact}
              style={{ background: 'none', border: 'none', color: '#C04A0E', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: '0.82rem' }}>
              Contact your coach
            </button>
          </p>
        </form>
      </div>

      {contactOpen && (
        <ContactCard
          contact={contact}
          loading={contactLoading}
          onClose={() => setContactOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Coach contact card (modal shown on "Need an account?" click) ────────────
function ContactCard({ contact, loading, onClose }) {
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16, zIndex: 200,
  };
  const card = {
    background: '#fff', color: '#1a1a1a', borderRadius: 14, padding: 24,
    maxWidth: 420, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
  };
  const link = { color: '#C04A0E', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-word' };
  const linkRow = { display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.95rem' };

  const phoneDigits = (contact?.phone || '').replace(/\D/g, '');
  const websiteHref = contact?.website
    ? (contact.website.startsWith('http') ? contact.website : `https://${contact.website}`)
    : null;
  const igHandle = (contact?.instagram || '').replace(/^@/, '');

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            {loading || !contact ? (
              <div style={{ color: '#666' }}>Loading…</div>
            ) : (
              <>
                <div style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '1.5rem', fontWeight: 800, color: '#C04A0E',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  {contact.coachName || 'Your Coach'}
                </div>
                {contact.title       && <div style={{ fontSize: '0.88rem', color: '#666',  marginTop: 2 }}>{contact.title}</div>}
                {contact.businessName && <div style={{ fontSize: '0.84rem', color: '#888', marginTop: 2, fontWeight: 600 }}>{contact.businessName}</div>}
              </>
            )}
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#999', padding: 4 }}
            aria-label="Close"
          >✕</button>
        </div>

        {!loading && contact?.message && (
          <p style={{ marginTop: 16, fontSize: '0.92rem', color: '#333', lineHeight: 1.55 }}>{contact.message}</p>
        )}

        {!loading && contact && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            {contact.phone && (
              <div style={linkRow}>
                <span>📞</span>
                <a href={`tel:${phoneDigits}`} style={link}>{contact.phone}</a>
              </div>
            )}
            {contact.email && (
              <div style={linkRow}>
                <span>✉</span>
                <a href={`mailto:${contact.email}`} style={link}>{contact.email}</a>
              </div>
            )}
            {contact.location && (
              <div style={linkRow}>
                <span>📍</span>
                <span style={{ color: '#555' }}>{contact.location}</span>
              </div>
            )}
            {websiteHref && (
              <div style={linkRow}>
                <span>🌐</span>
                <a href={websiteHref} target="_blank" rel="noopener noreferrer" style={link}>{contact.website}</a>
              </div>
            )}
            {igHandle && (
              <div style={linkRow}>
                <span>◎</span>
                <a href={`https://instagram.com/${igHandle}`} target="_blank" rel="noopener noreferrer" style={link}>@{igHandle}</a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
